import Alert from '@storeblocks/alert';
import { Button } from '@storeblocks/button';
import Modal from '@storeblocks/modal';
import { LoadingIndicatorSpinner } from '@storeblocks/spinner-loading-indicator';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { type FC, useState } from 'react';
import styled from 'styled-components';

import { useOrganizationName } from '@/api/organizations/hooks/useOrganizationName';
import {
  useEditOrganizationMutation,
  useOrganizationsQuery,
} from '@/api/organizations/organizationsApi';
import {
  useAddIHubDirectoryToOrganizationMutation,
  useDeleteIHubDirectoryFromOrganizationMutation,
  useIHubDirectoriesForOrganizationQuery,
  useIHubDirectoriesQuery,
} from '@/api/reports/reportsApi';
import { FormInput } from '@/components/Form/FormInput';
import { FormRow } from '@/components/Form/FormRow';
import { Show } from '@/components/Show';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

import { DirectorySelector } from './DirectorySelector';
import {
  EditOrganizationFormValues,
  getInitialValues,
  getValidationSchema,
} from './EditOrganizationFormSchema';

interface Props {
  closeModal: () => void;
  organizationId: string;
}

export const EditOrganizationModal: FC<Props> = ({
  closeModal,
  organizationId,
}) => {
  const [submitError, setSubmitError] = useState(false);
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const iHubDirectoriesQuery = useIHubDirectoriesQuery();

  const iHubDirectoriesForOrganizationQuery =
    useIHubDirectoriesForOrganizationQuery(organizationId);

  const [addIHubDirectoryToOrganization] =
    useAddIHubDirectoryToOrganizationMutation();

  const [deleteIHubDirectoryFromOrganization] =
    useDeleteIHubDirectoryFromOrganizationMutation();

  const { organizationName, organizationsIsPending } =
    useOrganizationName(organizationId);

  const organizationsQuery = useOrganizationsQuery();

  const [editOrganization] = useEditOrganizationMutation();

  const isPending =
    iHubDirectoriesForOrganizationQuery.isLoading ||
    iHubDirectoriesQuery.isLoading ||
    organizationsIsPending;

  const handleSubmit = (
    values: EditOrganizationFormValues,
    helpers: FormikHelpers<EditOrganizationFormValues>,
  ): void => {
    const promises: Promise<unknown>[] = [];

    if (organizationName !== values.orgName) {
      promises.push(
        editOrganization({ organizationId, organizationName: values.orgName }),
      );
    }

    if (
      !iHubDirectoriesForOrganizationQuery.isError &&
      !iHubDirectoriesQuery.isError
    ) {
      const customerIhubDirectoriesIds =
        iHubDirectoriesForOrganizationQuery.data?.map((dir) => dir.id) ?? [];

      const directoriesToAdd = values.directories.filter(
        (newDir) => !customerIhubDirectoriesIds.includes(newDir),
      );

      const directoriesToRemove = customerIhubDirectoriesIds.filter(
        (currentDir) => !values.directories.includes(currentDir),
      );

      directoriesToAdd.forEach((directoryId) => {
        promises.push(
          addIHubDirectoryToOrganization({
            organizationCmId: organizationId,
            directoryId,
          }),
        );
      });

      directoriesToRemove.forEach((directoryId) => {
        promises.push(
          deleteIHubDirectoryFromOrganization({
            organizationCmId: organizationId,
            directoryId,
          }),
        );
      });
    }

    Promise.all(promises)
      .then(() => {
        organizationsQuery.refetch();
        closeModal();
      })
      .catch(() => {
        setSubmitError(true);
        helpers.setSubmitting(false);
      });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(
        organizationName,
        iHubDirectoriesForOrganizationQuery.data ?? [],
      )}
      onSubmit={handleSubmit}
      validationSchema={getValidationSchema(texts)}
    >
      {({
        isSubmitting,
        dirty,
        errors,
        touched,
      }: FormikProps<EditOrganizationFormValues>) => (
        <Form id="edit-organization-form">
          <Modal
            trigger={undefined}
            open
            onClose={closeModal}
            title={texts.adminModal.editOrganization.title}
            content={
              <>
                <Show when={!isPending}>
                  <FormRow>
                    <FormInput
                      name="orgName"
                      label={texts.adminModal.orgName}
                      alert={{
                        message: touched.orgName ? errors.orgName : undefined,
                        messageType:
                          touched.orgName && errors.orgName
                            ? 'error'
                            : undefined,
                      }}
                    />
                  </FormRow>
                  <FormRow>
                    <DirectorySelector cmId={organizationId} />
                  </FormRow>
                  <Show when={submitError}>
                    <FormRow>
                      <Alert
                        variant="error"
                        description={texts.admin.organization.couldNotSave}
                      />
                    </FormRow>
                  </Show>
                </Show>
                <Show when={isPending}>
                  <SpinnerWrapper>
                    <LoadingIndicatorSpinner />
                  </SpinnerWrapper>
                </Show>
              </>
            }
            footer={
              <WithGap gap="16" direction="row" key="modal-button-container">
                <Button
                  type="submit"
                  form="edit-organization-form"
                  disabled={
                    isSubmitting || !dirty || Object.keys(errors).length > 0
                  }
                  data-trackid={getTrackId(
                    'save:edit-organization-modal',
                    TrackingElement.Button,
                  )}
                  loading={isSubmitting}
                >
                  {texts.adminModal.saveButton}
                </Button>
                <Button
                  type="button"
                  disabled={isSubmitting}
                  variant="subtle"
                  onClick={closeModal}
                  data-trackid={getTrackId(
                    'cancel:edit-organization-modal',
                    TrackingElement.Button,
                  )}
                >
                  {texts.adminModal.cancelButton}
                </Button>
              </WithGap>
            }
          ></Modal>
        </Form>
      )}
    </Formik>
  );
};

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
`;
